import { createFileRoute } from "@tanstack/react-router";

import { useIntegrations } from "@/hooks/use-integrations";
import { formatShortDate } from "@/utils";

type StatCardProps = {
  label: string;
  value: number | string;
  hint?: string;
};

const StatCard = ({ hint, label, value }: StatCardProps) => {
  return (
    <div className="flex flex-col gap-1 rounded-xl bg-surface p-4">
      <span className="text-sm text-content-muted">{label}</span>
      <span className="text-2xl font-semibold">{value}</span>
      {hint ? <span className="text-xs text-content-subtle">{hint}</span> : null}
    </div>
  );
};

const PersonifyOverviewPage = () => {
  const { data: stats, error, isLoading } = useIntegrations().getPersonifyStats();

  if (isLoading) {
    return <div className="text-sm text-content-muted">Loading…</div>;
  }

  if (error || !stats) {
    return (
      <div className="rounded-xl bg-danger-surface p-4 text-sm text-danger-content">
        Failed to load Personify stats.
        {error instanceof Error ? <p className="mt-1 text-xs">{error.message}</p> : null}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      <StatCard hint="Identities with a linked patient" label="Linked" value={stats.linkedCount} />
      <StatCard hint="Awaiting profiling completion" label="Pending" value={stats.pendingCount} />
      <StatCard label="Total" value={stats.totalCount} />
      <StatCard
        hint="oidc.flow_failed in the last 7 days"
        label="Recent failures"
        value={stats.recentFailureCount}
      />
      {stats.lastLoginAt ? (
        <StatCard label="Most recent login" value={formatShortDate(stats.lastLoginAt)} />
      ) : null}
    </div>
  );
};

export const Route = createFileRoute("/_private/integrations/personify/overview/")({
  component: PersonifyOverviewPage,
});
